import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { BaseService } from '../../services/base.service';
import { HelperService } from '../../helper.service';
import { InspectionConfig } from '../models/inspection-config.model';
import { InspectionReport, ReportFilters, ReportListItem, ReportListResponse } from '../models/inspection-report.model';

@Injectable({
  providedIn: 'root'
})
export class FieldInspectionService extends BaseService {

  private readonly apiUrl = this.API_BASE_URL + 'pin-management/inspection/';

  // Signals — templates read these directly
  loading = signal<boolean>(false);
  config = signal<InspectionConfig | null>(null);
  currentReport = signal<InspectionReport | null>(null);
  reportList = signal<ReportListItem[]>([]);
  totalReports = signal<number>(0);

  constructor(private http: HttpClient, private helper: HelperService) {
    super();
  }

  private get headers(): HttpHeaders {
    return new HttpHeaders({ 'x-tenant-id': String(this.helper.getTenantId() ?? '') });
  }

  loadConfig(crop: string, fieldType: string): void {
    this.loading.set(true);
    const params = new HttpParams().set('crop', crop).set('fieldType', fieldType);
    this.http.get<InspectionConfig>(
      this.API_BASE_URL + 'pin-management/inspection/config',
      { params, headers: this.headers }
    ).subscribe({
      next: (data) => {
        this.config.set(data);
        this.loading.set(false);
      },
      error: () => {
        // Set empty config so form steps don't show a stuck "Loading..." spinner
        this.config.set({ structuredObservations: [], pests: [], diseases: [], deficiencies: [] });
        this.loading.set(false);
      }
    });
  }

  loadReports(filters: ReportFilters): void {
    this.loading.set(true);
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params = params.set(key, String(value));
      }
    });
    this.http.get<ReportListResponse>(
      this.apiUrl + 'reports',
      { params, headers: this.headers }
    ).subscribe({
      next: (data) => {
        this.reportList.set(data.reports);
        this.totalReports.set(data.total);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  /** Returns an Observable so callers can subscribe and react when data arrives */
  loadReportsObservable(filters: ReportFilters): Observable<ReportListResponse> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<ReportListResponse>(
      this.apiUrl + 'reports',
      { params, headers: this.headers }
    );
  }

  loadReportDetail(reportId: string): Observable<InspectionReport> {
    this.loading.set(true);
    return this.http.get<InspectionReport>(
      this.apiUrl + `reports/${reportId}`,
      { headers: this.headers }
    ).pipe(
      tap(data => {
        this.currentReport.set(data);
        this.loading.set(false);
      }),
      catchError(err => {
        this.loading.set(false);
        return throwError(() => err);
      })
    );
  }

  createReport(payload: Partial<InspectionReport>): Observable<InspectionReport> {
    return this.http.post<InspectionReport>(
      this.apiUrl + 'reports', payload, { headers: this.headers }
    );
  }

  updateReport(reportId: string, payload: Partial<InspectionReport>): Observable<InspectionReport> {
    return this.http.put<InspectionReport>(
      this.apiUrl + `reports/${reportId}`, payload, { headers: this.headers }
    ).pipe(tap(updated => this.currentReport.set(updated)));
  }

  finaliseReport(reportId: string): Observable<InspectionReport> {
    return this.http.post<InspectionReport>(
      this.apiUrl + `reports/${reportId}/finalise`, {}, { headers: this.headers }
    ).pipe(tap(updated => this.currentReport.set(updated)));
  }

  /** POST /reports/{uuid}/retrigger-pdf — requeues the PDF for a FINALISED report.
   *  Requires the Inspector role. Generation is asynchronous, so a 200 confirms the
   *  request was queued, not that the email has been sent. */
  retriggerPdf(reportId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      this.apiUrl + `reports/${reportId}/retrigger-pdf`, {},
      { headers: this.headers }
    );
  }

  deleteReport(reportId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      this.apiUrl + `reports/${reportId}`,
      { headers: this.headers }
    );
  }

  /** GET /v2/pin-management/inspection/inspectors
   *  Requires InspectionRequestor or Inspector role.
   *  Backend also reads x-application-tenant-id on this endpoint. */
  getInspectors(): Observable<InspectorUser[]> {
    return this.http.get<{ inspectors: InspectorUser[] }>(
      this.apiUrl + 'inspectors',
      { headers: this.headers }
    ).pipe(map(res => res.inspectors ?? []));
  }
}

export interface InspectorUser {
  userId: number;
  email: string;
  name: string;
}
